import React, { ReactNode, memo } from "react";

import { Icon } from "@UI/Components/Icon/Icon";
import { CircleInfo } from "@UI/Components/Icons/CircleInfo";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from "@UI/Components/Form/Form";

export interface IAuthFormControlProps {
  label: ReactNode;
  /** Ties the label to the field, which must carry the same `id`. */
  htmlFor: string;
  /** Its presence is what marks the field invalid, so a caller never has to
   * keep two props in step. */
  errorMessage?: string;
  /** Renders the asterisk *and* marks the field required to a screen reader. */
  isRequired?: boolean;
  children: ReactNode;
}

/** The label/error shell the auth step's fields sit in. */
export const AuthFormControl = memo(
  ({
    label,
    htmlFor,
    errorMessage,
    isRequired,
    children,
  }: IAuthFormControlProps) => (
    <FormControl isInvalid={Boolean(errorMessage)} isRequired={isRequired}>
      <FormLabel
        htmlFor={htmlFor}
        fontSize="md"
        fontWeight="400"
        color="gray.700"
        marginBottom="8px"
        sx={{
          ".chakra-form__required-indicator": {
            marginStart: 0,
            color: "inherit",
          },
        }}
      >
        {label}
      </FormLabel>
      {children}
      {errorMessage && (
        <FormErrorMessage fontSize="sm" gap="6px">
          <Icon as={CircleInfo} width="14px" height="14px" aria-hidden />
          {errorMessage}
        </FormErrorMessage>
      )}
    </FormControl>
  )
);

AuthFormControl.displayName = "AuthFormControl";
